import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  ArrowRight,
  ArrowLeft,
  Mail,
  KeyRound,
  CheckCircle2,
  RefreshCw,
  Shield,
} from "lucide-react";
import { toast } from "react-toastify";
import ApiClient from "../api/apiClient";
import loader from "../loader";
import methodModel from "../methods";
import FormControl from "../../components/common/FormControl";
import { getTenantInfo } from "../../utils/domain";

export default function Forgot() {
  const history = useNavigate();
  const WebDetails = useSelector((state) => state.logo);
  const tenantInfo = getTenantInfo();

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({ email: "" });
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [sentId, setSentId] = useState("");

  // Cooldown countdown timer for Resend OTP
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Real-time Email Validation Handler matching Login page & Super Admin portal
  const handleEmailChange = (val) => {
    setEmail(val);
    const trimmed = String(val || "").trim();
    if (!trimmed) {
      setErrors({ email: "Please enter your email address." });
    } else {
      const check = methodModel.validateEmail(trimmed);
      setErrors({ email: check.success ? "" : check.message });
    }
  };

  // Submit Handler: calls user/admin/forgot/password
  const handleSubmit = (e) => {
    e.preventDefault();
    const emailTrimmed = (email || "").trim();

    // Validate email with exact same methodModel validator as Login page
    const emailValidation = methodModel.validateEmail(emailTrimmed);
    if (!emailValidation.success) {
      setErrors({ email: emailValidation.message });
      return;
    }

    setErrors({ email: "" });
    setLoading(true);
    loader(true);

    ApiClient.post(
      "user/admin/forgot/password",
      { email: emailTrimmed },
      "",
      true
    )
      .then((res) => {
        loader(false);
        setLoading(false);
        if (res?.success) {
          setCodeSent(true);
          setSentId(res?.id || res?.data?._id || res?.data?.id || "");
          setResendCooldown(30);
          toast.success(
            res?.message || "6-digit verification code sent to your email."
          );
        } else {
          // Fallback to customer endpoint user/forgot/password if admin lookup fails
          ApiClient.post(
            "user/forgot/password",
            { email: emailTrimmed },
            "",
            true
          )
            .then((custRes) => {
              if (custRes?.success) {
                setCodeSent(true);
                setSentId(custRes?.id || "");
                setResendCooldown(30);
                toast.success(
                  custRes?.message || "Verification code sent to your email."
                );
              } else {
                const errMsg =
                  custRes?.error?.message ||
                  custRes?.message ||
                  res?.error?.message ||
                  res?.message ||
                  "Unable to find account with this email.";
                setErrors({ email: errMsg });
                toast.error(errMsg);
              }
            })
            .catch(() => {
              const errMsg =
                res?.error?.message ||
                res?.message ||
                "Unable to find account with this email.";
              setErrors({ email: errMsg });
              toast.error(errMsg);
            });
        }
      })
      .catch((err) => {
        // Fallback to user/forgot/password
        ApiClient.post(
          "user/forgot/password",
          { email: emailTrimmed },
          "",
          true
        )
          .then((custRes) => {
            loader(false);
            setLoading(false);
            if (custRes?.success) {
              setCodeSent(true);
              setSentId(custRes?.id || "");
              setResendCooldown(30);
              toast.success(
                custRes?.message || "Verification code sent to your email."
              );
            } else {
              const errMsg =
                err?.response?.data?.error?.message ||
                err?.message ||
                "Failed to request password reset. Please try again.";
              setErrors({ email: errMsg });
              toast.error(errMsg);
            }
          })
          .catch(() => {
            loader(false);
            setLoading(false);
            const errMsg =
              err?.response?.data?.error?.message ||
              err?.message ||
              "Failed to request password reset. Please try again.";
            setErrors({ email: errMsg });
            toast.error(errMsg);
          });
      });
  };

  // Resend Handler: calls user/admin/resend-forgot-password-otp or forgot/password
  const handleResendOtp = () => {
    const emailTrimmed = (email || "").trim();
    if (!emailTrimmed) {
      setErrors({ email: "Please enter your email address." });
      return;
    }

    setResending(true);
    loader(true);

    ApiClient.post(
      "user/admin/forgot/password",
      { email: emailTrimmed },
      "",
      true
    )
      .then((res) => {
        loader(false);
        setResending(false);
        if (res?.success) {
          setResendCooldown(30);
          if (res?.id) setSentId(res.id);
          toast.success(
            res?.message || "A fresh 6-digit code has been emailed to you."
          );
        } else {
          const errMsg =
            res?.error?.message || res?.message || "Failed to resend code.";
          toast.error(errMsg);
        }
      })
      .catch((err) => {
        loader(false);
        setResending(false);
        const errMsg =
          err?.response?.data?.error?.message ||
          err?.message ||
          "Failed to resend verification code.";
        toast.error(errMsg);
      });
  };

  const logoSrc = WebDetails?.logo || "/assets/img/logo.png";

  return (
    <>
      <div className="h-screen w-full flex flex-col lg:flex-row bg-white lg:bg-[#081325] overflow-hidden antialiased selection:bg-[#115B9D] selection:text-white">
        {/* ======================================================== */}
        {/* LEFT BRAND & PLATFORM SHOWCASE PANEL (Visible on lg+) */}
        {/* ======================================================== */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[52%] h-full relative flex-col justify-between p-6 xl:p-10 2xl:p-12 overflow-hidden bg-gradient-to-br from-[#061224] via-[#0B2545] to-[#115B9D] text-white select-none">
          {/* Ambient Glow Orbs */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-1/2 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-1/3 w-80 h-80 bg-[#115B9D]/30 rounded-full blur-3xl pointer-events-none" />

          {/* Subtle Grid Pattern Overlay */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />

          {/* Top Branding Bar */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-white/15 shadow-xl">
              <img
                src={logoSrc}
                alt="Charles S. Smith & Associates"
                className="h-9 w-auto object-contain bg-white rounded-lg p-1 shadow-sm"
              />
              <div className="leading-tight">
                <span className="block text-[10px] uppercase font-bold tracking-widest text-sky-300">
                  E-Commerce Suite
                </span>
                <span className="block text-xs font-semibold text-white tracking-tight">
                  Charles S. Smith & Associates
                </span>
              </div>
            </div>

            {/* Live Status Pill */}
            <div className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-medium backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              Systems Operational
            </div>
          </div>

          {/* Center Showcase Content */}
          <div className="relative z-10 my-auto py-4 xl:py-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/15 border border-sky-300/30 text-sky-200 text-xs font-semibold uppercase tracking-wider mb-3">
              <KeyRound className="w-3.5 h-3.5 text-sky-300" />
              Password Recovery
            </div>

            <h1 className="text-2xl xl:text-3xl 2xl:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Enterprise Access Restoration For{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-sky-200 to-white">
                Platform Administrators
              </span>
            </h1>

            <p className="mt-2.5 text-xs xl:text-sm text-sky-100 leading-relaxed max-w-lg font-normal">
              Generate a cryptographically secure 6-digit verification code to safely recover your administrator account credentials.
            </p>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5 mt-5">
              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-sky-400/20 flex items-center justify-center text-sky-300 mb-2">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  6-Digit Code
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Dispatched instantly to your administrator inbox.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-indigo-400/20 flex items-center justify-center text-indigo-300 mb-2">
                  <Shield className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  SSL Protected
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  High-security token validation preventing breach.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-emerald-400/20 flex items-center justify-center text-emerald-300 mb-2">
                  <RefreshCw className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Resend Support
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  One-click fresh OTP generation if delayed.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Trust & Heritage Footer */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-sky-100">
            <span className="font-semibold text-white">
              Serving the Americas Since 1976
            </span>
            <span className="flex items-center gap-1.5 text-sky-200 font-medium">
              <Lock className="w-3.5 h-3.5 text-sky-300" /> 256-Bit SSL Encrypted
            </span>
          </div>
        </div>

        {/* ======================================================== */}
        {/* RIGHT FORM PANEL (Matches Screenshot 2) */}
        {/* ======================================================== */}
        <div className="w-full lg:w-1/2 xl:w-[48%] h-full flex items-center justify-center px-6 py-6 sm:px-10 sm:py-8 lg:px-12 xl:px-16 bg-white overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-[400px] my-auto py-2">
            {/* Header with Logo */}
            <div className="text-center mb-5 sm:mb-6">
              <img
                src={logoSrc}
                alt="Platform Logo"
                className="h-10 w-auto object-contain mx-auto mb-2"
              />
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                {codeSent ? "Check Your Email" : "Forgot Password?"}
              </h2>
              <p className="text-xs text-slate-700 mt-1 leading-relaxed font-medium">
                {codeSent
                  ? `A 6-digit verification code has been dispatched to ${email}.`
                  : "Enter your registered email to receive a 6-digit verification code."}
              </p>

            </div>

            {/* Code Sent Success State Card */}
            {codeSent ? (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div className="font-bold text-sm text-emerald-950">
                    Verification Code Dispatched!
                  </div>
                  <p className="text-xs text-emerald-800 leading-relaxed font-medium">
                    We sent a 6-digit verification code to{" "}
                    <strong className="text-emerald-950 break-all">{email}</strong>. Use it to set a new password.
                  </p>
                </div>

                {/* Primary Action: Go to Reset Password page with pre-filled email */}
                <button
                  type="button"
                  onClick={() =>
                    history(
                      `/reset-password?userId=${encodeURIComponent(
                        sentId
                      )}&email=${encodeURIComponent(email)}`
                    )
                  }
                  className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer"
                >
                  <span>Enter Code & Reset Password</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>

                {/* Secondary Action: Resend OTP with cooldown */}
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || resending}
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#115B9D] hover:text-[#0b4377] hover:underline transition-colors disabled:opacity-50 disabled:no-underline cursor-pointer"
                  >
                    <RefreshCw
                      className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`}
                    />
                    <span>
                      {resendCooldown > 0
                        ? `Resend Code in ${resendCooldown}s`
                        : resending
                        ? "Resending Code..."
                        : "Didn't receive the code? Resend Code"}
                    </span>
                  </button>
                </div>

                {/* Change Email Action */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setCodeSent(false);
                      setErrors({ email: "" });
                    }}
                    className="text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
                  >
                    Entered the wrong email?{" "}
                    <span className="underline font-bold text-slate-800">
                      Change Email
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              /* Request Form */
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {/* Email Field matching Login page */}
                <FormControl
                  type="email"
                  name="email"
                  id="forgotEmail"
                  label="Email Address"
                  required={true}
                  placeholder="superadmin@yopmail.com"
                  value={email}
                  error={errors.email}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  divClass="space-y-1"
                  labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                  className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
                />

                {/* Submit Action Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    <span>
                      {loading ? "Sending Code..." : "Send Verification Code"}
                    </span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>

                {/* Back to Sign In Action */}
                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-700 hover:text-[#115B9D] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Remember your password?</span>
                    <span className="font-bold text-[#115B9D] underline">
                      Sign In
                    </span>
                  </Link>
                </div>
              </form>
            )}

            {/* Security & Support Footer */}
            <div className="mt-6 pt-3.5 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Lock className="w-3.5 h-3.5 text-[#115B9D]" /> 256-Bit SSL Encrypted
              </span>
              <span className="font-semibold text-slate-800">
                Authorized Personnel Only
              </span>
            </div>

            {/* Copyright Note */}
            <p className="text-center text-[11px] text-slate-600 mt-2 font-medium">
              &copy; {new Date().getFullYear()} Charles S. Smith & Associates. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
